import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Activity, Database, Zap, Mail, Globe, TrendingUp,
  CheckCircle2, XCircle, AlertCircle, RefreshCw, ArrowLeft, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 30000;

const TONE = {
  ok:       { bg: 'bg-emerald-50 dark:bg-emerald-500/10',  border: 'border-emerald-200 dark:border-emerald-500/30', text: 'text-emerald-700 dark:text-emerald-300', icon: CheckCircle2, label: 'Operational', dot: 'bg-emerald-500' },
  degraded: { bg: 'bg-amber-50   dark:bg-amber-500/10',    border: 'border-amber-200   dark:border-amber-500/30',   text: 'text-amber-700   dark:text-amber-300',   icon: AlertCircle,  label: 'Degraded',    dot: 'bg-amber-500' },
  down:     { bg: 'bg-rose-50    dark:bg-rose-500/10',     border: 'border-rose-200    dark:border-rose-500/30',    text: 'text-rose-700    dark:text-rose-300',    icon: XCircle,      label: 'Down',        dot: 'bg-rose-500' },
  neutral:  { bg: 'bg-neutral-50 dark:bg-white/5',         border: 'border-neutral-200 dark:border-white/10',       text: 'text-neutral-600 dark:text-neutral-400', icon: AlertCircle,  label: 'Not configured', dot: 'bg-neutral-400' },
};

const toTone = (s) => {
  if (s === 'up' || s === 'configured' || s === 'ok') return TONE.ok;
  if (s === 'degraded') return TONE.degraded;
  if (s === 'down') return TONE.down;
  return TONE.neutral;
};

const AdminHealthPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate('/');
  }, [user, loading, navigate]);

  const fetchHealth = async () => {
    setBusy(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/health`);
      setData(res); setErr(null); setLastFetched(new Date());
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!user?.is_admin) return;
    fetchHealth();
    const id = setInterval(fetchHealth, REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [user]);

  if (loading || !user) return <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>;
  if (!user.is_admin) return null;

  const overallTone = data ? toTone(data.overall) : TONE.neutral;
  const services = data?.services || {};
  const orders = data?.orders || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <Link to="/admin" className="text-xs text-[#8247E5] hover:underline inline-flex items-center gap-1 mb-2" data-testid="admin-health-back">
            <ArrowLeft className="w-3 h-3" /> Back to admin
          </Link>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2 dark:text-white">
            <Activity className="w-6 h-6 text-[#8247E5]" /> System health
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Live status of every service that powers TokenGate. Refreshes automatically every 30 seconds.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastFetched && (
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              Updated {lastFetched.toLocaleTimeString()}
            </span>
          )}
          <Button variant="outline" onClick={fetchHealth} disabled={busy} className="gap-2" data-testid="admin-health-refresh">
            <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {err && (
        <div className="mb-4 rounded-xl border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 p-4 text-sm text-rose-700 dark:text-rose-300">
          Failed to load health data: {err}
        </div>
      )}

      {/* Overall banner */}
      {data && (
        <div className={`rounded-2xl border p-5 mb-6 flex items-center justify-between ${overallTone.bg} ${overallTone.border}`} data-testid="admin-health-overall">
          <div className="flex items-center gap-4">
            <div className="relative">
              <span className={`absolute -inset-1 rounded-full ${overallTone.dot} opacity-30 animate-ping`} />
              <span className={`relative block w-3 h-3 rounded-full ${overallTone.dot}`} />
            </div>
            <div>
              <div className={`font-display text-xl font-bold ${overallTone.text}`}>
                {data.overall === 'ok' ? 'All systems operational' : data.overall === 'degraded' ? 'Some services degraded' : 'Services down'}
              </div>
              <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                Last checked {new Date(data.checked_at).toLocaleString()}
              </div>
            </div>
          </div>
          <div className={`text-[10px] uppercase tracking-widest font-bold ${overallTone.text}`}>{overallTone.label}</div>
        </div>
      )}

      {/* Service cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <ServiceCard icon={Database} name="MongoDB" service={services.mongo} extra={services.mongo?.latency_ms != null ? `${services.mongo.latency_ms}ms` : null} testid="svc-mongo" />
        <ServiceCard icon={Zap} name="NOWPayments" service={services.nowpayments} extra={services.nowpayments?.latency_ms != null ? `${services.nowpayments.latency_ms}ms` : (services.nowpayments?.configured === false ? 'API key missing' : null)} testid="svc-nowp" />
        <ServiceCard icon={Mail} name="Resend (email)" service={services.resend} extra={services.resend?.from || null} testid="svc-resend" />
        <ServiceCard icon={Globe} name="Google OAuth" service={services.google_oauth} extra={null} testid="svc-google" />
        <PricesProxyCard proxy={services.prices_proxy} testid="svc-prices" />
      </div>

      {/* Order volume */}
      <div className="rounded-2xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-neutral-900/50 p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-[#8247E5]" />
          <div className="text-xs uppercase tracking-widest font-semibold text-neutral-500 dark:text-neutral-400">Order volume</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Metric label="Last 24h · paid orders"     value={orders.last_24h ?? '—'} accent testid="m-24h-orders" />
          <Metric label="Last 24h · revenue"         value={orders.last_24h_usd != null ? `$${orders.last_24h_usd.toFixed(2)}` : '—'} testid="m-24h-usd" />
          <Metric label="Last 7d · paid orders"      value={orders.last_7d ?? '—'} testid="m-7d-orders" />
          <Metric label="Last 7d · revenue"          value={orders.last_7d_usd != null ? `$${orders.last_7d_usd.toFixed(2)}` : '—'} testid="m-7d-usd" />
        </div>
      </div>
    </div>
  );
};

// ─── Service card ─────────────────────────────────────────────────────────
const ServiceCard = ({ icon: Icon, name, service, extra, testid }) => {
  const tone = toTone(service?.status);
  const ToneIcon = tone.icon;
  return (
    <div className={`rounded-2xl border p-5 ${tone.bg} ${tone.border}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
          <div className="font-semibold text-neutral-900 dark:text-white">{name}</div>
        </div>
        <ToneIcon className={`w-4 h-4 ${tone.text}`} />
      </div>
      <div className={`text-2xl font-display font-bold ${tone.text}`}>{tone.label}</div>
      {extra && <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{extra}</div>}
    </div>
  );
};

// ─── Prices proxy card (has extra telemetry) ───────────────────────────────
const PricesProxyCard = ({ proxy, testid }) => {
  const hits = proxy?.hits ?? 0;
  const misses = proxy?.misses ?? 0;
  const errors = proxy?.errors ?? 0;
  const ratio = proxy?.hit_ratio_pct;
  const errRate = (hits + misses) > 0 ? ((errors / (hits + misses)) * 100).toFixed(1) : null;
  const status = errRate != null && errRate > 25 ? 'degraded' : (hits + misses > 0 || errors === 0 ? 'up' : 'up');
  const tone = toTone(status);
  const ToneIcon = tone.icon;
  return (
    <div className={`rounded-2xl border p-5 ${tone.bg} ${tone.border}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
          <div className="font-semibold text-neutral-900 dark:text-white">CoinGecko proxy</div>
        </div>
        <ToneIcon className={`w-4 h-4 ${tone.text}`} />
      </div>
      <div className={`text-2xl font-display font-bold ${tone.text}`}>
        {ratio != null ? `${ratio}% cache hit` : tone.label}
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] text-neutral-600 dark:text-neutral-400">
        <div><b className="text-neutral-900 dark:text-white">{hits}</b> hits</div>
        <div><b className="text-neutral-900 dark:text-white">{misses}</b> misses</div>
        <div><b className={errors > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-neutral-900 dark:text-white'}>{errors}</b> errors</div>
      </div>
    </div>
  );
};

const Metric = ({ label, value, accent = false, testid }) => (
  <div className={`rounded-xl p-3 ${accent ? 'bg-gradient-to-br from-violet-50 to-white dark:from-violet-500/10 dark:to-transparent border border-violet-200 dark:border-violet-500/30' : 'border border-neutral-200 dark:border-white/10'}`} data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-neutral-500 dark:text-neutral-400 font-semibold">{label}</div>
    <div className={`font-display text-2xl font-bold mt-1 ${accent ? 'text-[#8247E5] dark:text-[#C084FC]' : 'text-neutral-900 dark:text-white'}`}>{value}</div>
  </div>
);

export default AdminHealthPage;
